'use client';
import { useState } from 'react';
import { autoDir } from '@/lib/autoDir';
import api, { setToken } from '@/lib/api';
import { setStoredUser } from '@/lib/permissions';
import { useRouter } from 'next/navigation';

export default function LoginPage() {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // حالة تغيير كلمة المرور الإجباري
  const [mustChange, setMustChange] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);
  const [changeErr, setChangeErr] = useState<string | null>(null);

  const router = useRouter();

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username: loginId, password });
      const { access_token, user, must_change_password } = res.data;
      setToken(access_token || null);
      // حفظ بيانات المستخدم والصلاحيات
      if (user) {
        setStoredUser({
          id: user.id,
          username: user.username,
          email: user.email,
          role: user.role,
          permissions: user.permissions || [],
        });
      }
      // هل يجب تغيير كلمة المرور؟
      if (must_change_password) {
        setCurrentPwd(password); // نحتفظ بكلمة المرور الحالية
        setMustChange(true);
        return;
      }
      router.replace('/dashboard');
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message;
      setErr(
        msg === 'missing_credentials'
          ? 'يرجى إدخال اسم المستخدم/البريد وكلمة المرور.'
          : msg === 'invalid_credentials'
          ? 'بيانات الدخول غير صحيحة.'
          : 'فشل تسجيل الدخول.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setChangeErr(null);
    if (newPwd.length < 8) { setChangeErr('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); return; }
    if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(newPwd)) { setChangeErr('كلمة المرور يجب أن تحتوي على حرف ورقم على الأقل'); return; }
    if (newPwd !== newPwd2) { setChangeErr('كلمتا المرور غير متطابقتين'); return; }
    setChangingPwd(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPwd,
        new_password: newPwd,
      });
      router.replace('/dashboard');
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setChangeErr(typeof d === 'string' ? d.split(': ').pop() || d : 'فشل تغيير كلمة المرور');
    } finally {
      setChangingPwd(false);
    }
  }

  // ── عرض نموذج تغيير كلمة المرور الإجباري ──
  if (mustChange) {
    return (
      <main className="min-h-dvh grid place-items-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img src="/images/logo.png" alt="مركز الإعلام الذكي" className="mx-auto h-16" />
            <h1 className="text-3xl font-extrabold text-th-text mt-2">تغيير كلمة المرور</h1>
            <p className="text-sm text-th-text-muted">يجب تغيير كلمة المرور قبل المتابعة</p>
          </div>
          <div className="bg-th-surface rounded-2xl shadow-md p-6 md:p-8">
            {changeErr && (
              <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                {changeErr}
              </div>
            )}
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm mb-1">كلمة المرور الجديدة</label>
                <input
                  type="password"
                  className="w-full rounded-md border border-th-border-strong px-3 py-2 focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                  placeholder="8 أحرف على الأقل (حرف + رقم)"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  autoComplete="new-password"
                  dir={autoDir(newPwd)}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm mb-1">تأكيد كلمة المرور الجديدة</label>
                <input
                  type="password"
                  className="w-full rounded-md border border-th-border-strong px-3 py-2 focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                  placeholder="أعد كتابة كلمة المرور"
                  value={newPwd2}
                  onChange={(e) => setNewPwd2(e.target.value)}
                  autoComplete="new-password"
                  dir={autoDir(newPwd2)}
                />
              </div>
              <button
                type="submit"
                disabled={changingPwd}
                className="w-full rounded-md bg-accent-700 px-4 py-2 text-accent-foreground hover:bg-accent-800 disabled:opacity-50"
              >
                {changingPwd ? 'جاري التحديث…' : 'تغيير كلمة المرور والمتابعة'}
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh grid place-items-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/images/logo.png" alt="مركز الإعلام الذكي" className="mx-auto h-16" />
          <h1 className="text-3xl font-extrabold text-th-text mt-2">مركز الإعلام الذكي</h1>
          <p className="text-sm text-th-text-muted">لوحة التحكم والإدارة</p>
        </div>

        <div className="bg-th-surface rounded-2xl shadow-md p-6 md:p-8">
          {err && (
            <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              {err}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm mb-1">اسم المستخدم أو البريد الإلكتروني</label>
              <input
                className="w-full rounded-md border border-th-border-strong px-3 py-2 focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                placeholder="admin أو name@example.com"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                autoComplete="username"
                dir={autoDir(loginId)}
              />
            </div>

            <div>
              <label className="block text-sm mb-1">كلمة المرور</label>
              <input
                type="password"
                className="w-full rounded-md border border-th-border-strong px-3 py-2 focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                dir={autoDir(password)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-accent-700 px-4 py-2 text-accent-foreground hover:bg-accent-800 disabled:opacity-50"
            >
              {loading ? 'جاري الدخول…' : 'دخول'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
